import { Route, RouteConfig } from 'vue-router';
import { addFunds } from '@store/modules/add-funds';
import { AddFundsUpgradeRoute } from 'types/add-funds';
import { addFundsUpgradeRouteOrder } from '@src/constants';
import { app } from '@store/modules/app';
import { flattenQueryParam } from '@src/utils/request';
import { investmentEntity } from '@src/store/modules/investment-entity';
import { redirect } from '@src/utils/client';

const getFlowStepIndex = (name: AddFundsUpgradeRoute): number => {
	return addFundsUpgradeRouteOrder.indexOf(name) + 1;
};

const metaConstructor = (route: Route): object => ({
	step: getFlowStepIndex(route.name as AddFundsUpgradeRoute)
});

const addFundsEntryRedirectHandling = (): string | null => {
	switch (investmentEntity.additionalInvestmentEligibility) {
		case 'ENTITY_NO_INVESTMENTS':
			return '/start-investing';
		case 'ENTITY_MISSING_INFORMATION':
			switch (investmentEntity.entityType) {
				case 'INDIVIDUAL':
					return `/account/investment-accounts/${investmentEntity.investmentEntityId}/individual-account/personal-information/edit`;
				case 'JOINT_ACCOUNT':
					return `/account/investment-accounts/${investmentEntity.investmentEntityId}/joint-account/account-holder-information/edit`;
				case 'TRUST':
					return `/account/investment-accounts/${investmentEntity.investmentEntityId}/trust/information/edit`;
				case 'LLC':
					return `/account/investment-accounts/${investmentEntity.investmentEntityId}/entity-account/entity-information/edit`;
			}
	}
	return null;
};

/* eslint-disable @typescript-eslint/explicit-function-return-type */
const addFundsRoutes: Array<RouteConfig> = [
	{
		path: `/account/repeat-investment/ria`,
		name: 'add-funds-ria',
		component: () => import(/* webpackChunkName: "add-funds-ria" */ '@views/account/add-funds/add-funds-ria.vue'),
		beforeEnter: async (to, from, next) => {
			if (investmentEntity.additionalInvestmentEligibility !== 'VALID' || investmentEntity.entityType === 'IRA') {
				const addFundsSpecialRedirect = addFundsEntryRedirectHandling();
				if (addFundsSpecialRedirect) {
					redirect(addFundsSpecialRedirect);
				} else {
					next({ name: 'account-overview', replace: true });
					app.ADD_TOAST({
						type: 'error',
						message: 'The current account is not eligible for an additional investment at this time.'
					});
				}
			} else {
				if (addFunds.orderComplete) {
					addFunds.clearPreviousOrder();
				}
				if (!addFunds.dataLoaded) {
					await addFunds.fetchAndStoreRepeatInvestmentMetadata();
				}
				next();
			}
		},
		redirect: { name: 'add-funds-ria-amount' },
		children: [
			{
				path: 'amount',
				name: 'add-funds-ria-amount',
				meta: metaConstructor,
				beforeEnter: async (to, from, next) => {
					const defaultAmountParam = flattenQueryParam(to.query.defaultAmount);
					addFunds.parseDefaultAmountParam(defaultAmountParam);
					next();
				},
				component: () =>
					import(
						/* webpackChunkName: "add-funds-ria-amount" */ '@views/account/add-funds/add-funds-ria-amount.vue'
					)
			},
			{
				path: 'agreements',
				name: 'add-funds-ria-agreements',
				meta: metaConstructor,
				beforeEnter: async (to, from, next) => {
					if (!addFunds.amount || !addFunds.paymentMethodId || !addFunds.reitInvestmentOrderGroupId) {
						next({ name: 'add-funds-ria-amount', replace: true });
					} else {
						next();
					}
				},
				component: () =>
					import(
						/* webpackChunkName: "add-funds-ria-agreements" */ '@views/account/add-funds/add-funds-ria-agreements.vue'
					)
			},
			{
				path: 'review',
				name: 'add-funds-ria-review',
				meta: metaConstructor,
				beforeEnter: async (to, from, next) => {
					if (
						!addFunds.amount ||
						!addFunds.reitInvestmentOrderGroupId ||
						addFunds.reitDocumentRevisionIds.length < 1
					) {
						next({ name: 'add-funds-ria-amount', replace: true });
					} else {
						next();
					}
				},
				component: () =>
					import(
						/* webpackChunkName: "add-funds-ria-review" */ '@views/account/add-funds/add-funds-ria-review.vue'
					)
			},
			{
				path: 'success',
				name: 'add-funds-ria-success',
				meta: metaConstructor,
				beforeEnter: async (to, from, next) => {
					if (!addFunds.reitInvestmentOrderGroupId) {
						next({ name: 'add-funds-ria-amount', replace: true });
					} else {
						next();
					}
				},
				component: () =>
					import(
						/* webpackChunkName: "add-funds-ria-success" */ '@views/account/add-funds/add-funds-ria-success.vue'
					)
			}
		]
	}
];

export default addFundsRoutes;
